import React, { useState, useEffect } from "react";
import {
    Box, Typography, Grid, Card, CardContent, Stack,
    CircularProgress, LinearProgress, Avatar,
    useTheme, Paper, Tooltip, IconButton
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SavingsIcon from "@mui/icons-material/Savings";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CategoryIcon from "@mui/icons-material/Category";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { motion } from "framer-motion";
import api from "../api/axios";

const MotionCard = motion(Card);

export default function Historico() {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [expenses, setExpenses] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [byStore, setByStore] = useState([]);
    const [byCategory, setByCategory] = useState([]);
    const [error, setError] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [expRes, topRes, sumRes, storeRes, catRes] = await Promise.all([
                api.get("/stats/expenses"),
                api.get("/stats/top-products"),
                api.get("/stats/summary"),
                api.get("/stats/by-store"),
                api.get("/stats/by-category")
            ]);
            setExpenses(expRes.data);
            setTopProducts(topRes.data);
            setSummary(sumRes.data);
            setByStore(storeRes.data);
            setByCategory(catRes.data);
        } catch (err) {
            console.error("Error al cargar estadísticas:", err);
            setError("No se pudieron cargar los datos estadísticos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80vh", gap: 2 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="body1" sx={{ fontWeight: 600, opacity: 0.6 }}>
                    Analizando tus datos de ahorro...
                </Typography>
            </Box>
        );
    }

    const maxExpense = expenses.length > 0 ? Math.max(...expenses.map(e => e.total)) : 0;
    const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

    const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
        <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            sx={{
                borderRadius: 4,
                boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                background: `linear-gradient(135deg, #fff 0%, ${color}10 100%)`,
                border: "1px solid",
                borderColor: "divider",
                height: "100%"
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, mb: 1 }}>
                            {title}
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5 }}>
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {trend === "up" && <ArrowUpwardIcon fontSize="inherit" color="error" />}
                                {trend === "down" && <ArrowDownwardIcon fontSize="inherit" color="success" />}
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    <Avatar sx={{ bgcolor: color, width: 56, height: 56, boxShadow: `0 4px 12px ${color}40` }}>
                        {icon}
                    </Avatar>
                </Stack>
            </CardContent>
        </MotionCard>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, margin: "0 auto" }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
                <Avatar sx={{ bgcolor: "primary.main", width: 60, height: 60, boxShadow: theme.shadows[4] }}>
                    <HistoryIcon sx={{ fontSize: 35 }} />
                </Avatar>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: "text.primary", lineHeight: 1.2 }}>
                        Mis Estadísticas
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.7, fontWeight: 500 }}>
                        Visualiza tu impacto económico y hábitos de consumo inteligente.
                    </Typography>
                </Box>
            </Stack>

            {error && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: "error.lighter", color: "error.main", borderRadius: 2 }}>
                    <Typography>{error}</Typography>
                </Paper>
            )}

            {/* Resumen de Métricas */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Gastado"
                        value={`${summary?.total_historico?.toFixed(2)}€`}
                        icon={<ShoppingCartIcon />}
                        color={theme.palette.primary.main}
                        subtitle={summary?.mes_actual > 0 ? `${summary.mes_actual.toFixed(2)}€ este mes` : "Sin gastos este mes"}
                        trend={summary?.mes_actual > summary?.mes_anterior ? "up" : "down"}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Ahorro Estimado"
                        value={`${summary?.ahorro_estimado?.toFixed(2)}€`}
                        icon={<SavingsIcon />}
                        color={theme.palette.success.main}
                        subtitle="Comparando con precios medios"
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Productos"
                        value={summary?.total_articulos}
                        icon={<ReceiptIcon />}
                        color={theme.palette.info.main}
                        subtitle="Comprados a través de la app"
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Supermercados"
                        value={byStore.length}
                        icon={<StorefrontIcon />}
                        color={theme.palette.warning.main}
                        subtitle="Tiendas de confianza visitadas"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Evolución Mensual */}
                <Grid item xs={12} lg={8}>
                    <Card sx={{ borderRadius: 4, height: "100%", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 4 }}>
                                <TrendingUpIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Evolución de Gastos
                                </Typography>
                                <Tooltip title="Muestra el total gastado en productos marcados como comprados cada mes">
                                    <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                                </Tooltip>
                            </Stack>

                            {expenses.length === 0 ? (
                                <Box sx={{ height: 300, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.02)", borderRadius: 4, border: "2px dashed rgba(0,0,0,0.05)" }}>
                                    <HistoryIcon sx={{ fontSize: 48, opacity: 0.1, mb: 2 }} />
                                    <Typography variant="body1" sx={{ opacity: 0.5, fontWeight: 600 }}>
                                        No hay datos históricos suficientes.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ height: 300, mt: 2, display: "flex", alignItems: "flex-end", gap: { xs: 1, md: 3 }, px: 2 }}>
                                    {expenses.map((e, idx) => (
                                        <Stack key={idx} alignItems="center" sx={{ flex: 1, height: "100%", justifyContent: "flex-end" }}>
                                            <motion.div
                                                initial={{ height: 0 }}
                                                animate={{ height: `${(e.total / maxExpense) * 220}px` }}
                                                transition={{ duration: 1, delay: idx * 0.1 }}
                                                style={{
                                                    width: "100%",
                                                    background: "linear-gradient(to top, #1976d2, #64b5f6)",
                                                    borderRadius: "8px 8px 4px 4px",
                                                    position: "relative",
                                                    minHeight: 4,
                                                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)"
                                                }}
                                            >
                                                <Typography variant="caption" sx={{
                                                    position: "absolute",
                                                    top: -25,
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    fontWeight: 800,
                                                    color: "primary.main"
                                                }}>
                                                    {e.total.toFixed(0)}€
                                                </Typography>
                                            </motion.div>
                                            <Typography variant="caption" sx={{ mt: 1.5, fontWeight: 700, opacity: 0.7 }}>
                                                {months[e.month - 1]}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Gastos por Tienda */}
                <Grid item xs={12} lg={4}>
                    <Card sx={{ borderRadius: 4, height: "100%", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                <StorefrontIcon color="warning" />
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Distribución por Tienda
                                </Typography>
                            </Stack>

                            <Stack spacing={3}>
                                {byStore.length === 0 ? (
                                    <Typography variant="body2" sx={{ py: 4, textAlign: "center", opacity: 0.5 }}>
                                        No hay datos de tiendas.
                                    </Typography>
                                ) : byStore.map((s, idx) => (
                                    <Box key={idx}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                                                <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: s.tienda === "Mercadona" ? "#00a650" : "#ee2c2c" }}>
                                                    {s.tienda[0]}
                                                </Avatar>
                                                {s.tienda}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                {s.total.toFixed(2)}€
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(s.total / summary.total_historico) * 100}
                                            sx={{
                                                height: 10,
                                                borderRadius: 5,
                                                bgcolor: "rgba(0,0,0,0.05)",
                                                "& .MuiLinearProgress-bar": {
                                                    borderRadius: 5,
                                                    bgcolor: s.tienda === "Mercadona" ? "#00a650" : "#ee2c2c"
                                                }
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Gastos por Categoría */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                <CategoryIcon color="secondary" />
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Gasto por Categoría
                                </Typography>
                            </Stack>

                            <Stack spacing={2.5}>
                                {byCategory.length === 0 ? (
                                    <Typography variant="body2" sx={{ py: 4, textAlign: "center", opacity: 0.5 }}>
                                        Empieza a comprar para ver el desglose.
                                    </Typography>
                                ) : byCategory.slice(0, 6).map((c, idx) => (
                                    <Box key={idx}>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>{c.categoria}</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{c.total.toFixed(2)}€</Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(c.total / byCategory[0].total) * 100}
                                            color="secondary"
                                            sx={{ height: 6, borderRadius: 3, opacity: 0.8 }}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Top Productos */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                <ReceiptIcon color="info" />
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Productos Favoritos
                                </Typography>
                            </Stack>

                            <Stack spacing={2}>
                                {topProducts.length === 0 ? (
                                    <Typography variant="body2" sx={{ py: 4, textAlign: "center", opacity: 0.5 }}>
                                        No hay suficientes datos.
                                    </Typography>
                                ) : topProducts.map((p, idx) => (
                                    <Paper
                                        key={idx}
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 3,
                                            bgcolor: "rgba(0,0,0,0.02)",
                                            border: "1px solid rgba(0,0,0,0.05)",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2
                                        }}
                                    >
                                        <Avatar sx={{ bgcolor: theme.palette.info.light, color: "info.contrastText", fontWeight: 900 }}>
                                            {idx + 1}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.nombre}</Typography>
                                            <Typography variant="caption" color="text.secondary">Comprado {p.count} veces</Typography>
                                        </Box>
                                        <Tooltip title="Frecuencia de compra">
                                            <CircularProgress
                                                variant="determinate"
                                                value={(p.count / topProducts[0].count) * 100}
                                                size={32}
                                                thickness={6}
                                                sx={{ color: theme.palette.info.main }}
                                            />
                                        </Tooltip>
                                    </Paper>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}